import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { WSBFWordmark } from "./WSBFWordmark";
import { Search, LogOut, LayoutDashboard, Menu, X, ChevronDown, Settings, Shield } from "lucide-react";

const NAV = [
  { to: "/players", label: "Players" },
  { to: "/clubs", label: "Clubs" },
  { to: "/federations", label: "Federations" },
  { to: "/competitions", label: "Competitions" },
  { to: "/cup", label: "Cup" },
  { to: "/continental", label: "Europe" },
  { to: "/rankings", label: "Rankings" },
  { to: "/live", label: "News" },
];

const MORE = [
  { to: "/shop", label: "Shop" },
  { to: "/strike5", label: "Strike 5" },
  { to: "/challenge", label: "Challenge" },
  { to: "/nations", label: "Nations" },
  { to: "/worlds", label: "World Nations" },
  { to: "/world-championship", label: "World Players" },
  { to: "/season-race", label: "Season" },
  { to: "/hall-of-fame", label: "Records" },
  { to: "/matches", label: "Matches" },
  { to: "/live", label: "Live" },
  { to: "/rivalry", label: "Rivalries" },
  { to: "/verify", label: "Verify" },
  { to: "/about", label: "About" },
];

export function Navbar() {
  const { user, hasRole, isAdmin, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => { setMoreOpen(false); setSearchOpen(false); setOpen(false); }, [loc.pathname]);

  // Logged-in sessions carry a wider right cluster (bell + dashboard + settings + logout,
  // plus Admin for staff) that cannot fit the full desktop bar until 2xl; logged-out stays at xl.
  const navShowCls = user ? "hidden 2xl:flex" : "hidden xl:flex";
  const hamCls = user ? "2xl:hidden" : "xl:hidden";

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/players?search=${encodeURIComponent(q.trim())}`);
  };

  const dashPath = hasRole("WSBF_ADMIN", "FEDERATION_ADMIN")
    ? "/admin/federation"
    : hasRole("TEAM_OWNER")
    ? "/dashboard/owner"
    : "/dashboard";

  const ROOT_ALIAS = {
    "/players": ["/players", "/player/"],
    "/clubs": ["/clubs", "/club/"],
    "/federations": ["/federations", "/federation/"],
    "/competitions": ["/competitions", "/competition/"],
  };
  const isActive = (to) => {
    const al = ROOT_ALIAS[to];
    if (al) return al.some((p) => loc.pathname === p || loc.pathname === p.replace(/\/$/, "") || loc.pathname.startsWith(p));
    return loc.pathname === to || loc.pathname.startsWith(to + "/");
  };

  const linkCls = (to) =>
    `relative px-2 py-1.5 text-[13px] font-bold uppercase tracking-wide ${
      isActive(to) ? "text-[#E5B842]" : "text-slate-200 hover:text-[#E5B842]"
    }`;

  const activeBar = (to) =>
    isActive(to) ? (
      <span className="absolute left-2.5 right-2.5 -bottom-0.5 h-0.5 rounded-full bg-[#E5B842]" style={{ boxShadow: "0 0 10px rgba(229,184,66,0.85)" }} />
    ) : null;

  return (
    <header className="sticky top-0 z-50 w-full backdrop-blur-xl bg-[#000000]/95 border-b border-[rgba(212,175,55,0.22)]">
      <div className="max-w-[1920px] mx-auto px-3 sm:px-8 lg:px-12 xl:px-8 h-16 flex items-center gap-2 sm:gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <WSBFWordmark size="sm" lockup={true} lockupClassName="hidden sm:block" />
        </Link>

        <nav className={`${navShowCls} items-center gap-0.5 mx-auto`}>
          {NAV.map((n) => (
            <Link key={n.to + n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`} className={linkCls(n.to)} style={{ transition: "color 0.15s ease" }}>
              {n.label}
              {activeBar(n.to)}
            </Link>
          ))}
          <div className="relative">
            <button
              data-testid="nav-more"
              onClick={() => setMoreOpen((o) => !o)}
              className="flex items-center gap-1 px-2 py-1.5 text-[13px] font-bold uppercase tracking-wide text-slate-200 hover:text-[#E5B842]"
              style={{ transition: "color 0.15s ease" }}
            >
              More <ChevronDown className={`w-3.5 h-3.5 ${moreOpen ? "rotate-180" : ""}`} style={{ transition: "transform 0.15s ease" }} />
            </button>
            {moreOpen && (
              <div className="absolute right-0 mt-2 w-48 rounded-lg bg-[#0a0a0a] border border-[rgba(212,175,55,0.25)] shadow-2xl py-2" data-testid="nav-more-menu">
                {MORE.map((m) => (
                  <Link key={m.to + m.label} to={m.to} data-testid={`nav-more-${m.label.toLowerCase().replace(/\s+/g, "-")}`} className="block px-4 py-2 text-[13px] font-bold uppercase tracking-wide text-slate-200 hover:text-[#E5B842] hover:bg-[#1a1407]" style={{ transition: "color 0.15s ease, background-color 0.15s ease" }}>
                    {m.label}
                  </Link>
                ))}
                {hasRole("WSBF_INTEGRITY_OFFICER", "WSBF_DISCIPLINARY_OFFICER", "WSBF_APPEALS_OFFICER", "WSBF_ADMIN") && (
                  <Link to="/integrity" className="block px-4 py-2 text-[13px] font-bold uppercase tracking-wide text-slate-200 hover:text-[#E5B842] hover:bg-[#1a1407]">
                    Integrity
                  </Link>
                )}
              </div>
            )}
          </div>
        </nav>

        <div className={`${navShowCls} items-center gap-3 shrink-0`}>
          {searchOpen ? (
            <form onSubmit={doSearch} className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                autoFocus
                data-testid="nav-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onBlur={() => !q && setSearchOpen(false)}
                placeholder="Search name or WSTF ID"
                className="bg-[#141414] border border-[rgba(212,175,55,0.22)] rounded-lg pl-9 pr-3 py-1.5 text-sm text-slate-200 w-52 focus:outline-none focus:border-[#E5B842]/60 focus:ring-1 focus:ring-[#E5B842]/40"
              />
            </form>
          ) : (
            <button data-testid="nav-search" onClick={() => setSearchOpen(true)} className="p-2 text-slate-200 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }} title="Search">
              <Search className="w-5 h-5" />
            </button>
          )}

          {user ? (
            <>
              <NotificationBell />
              {isAdmin && (
                <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#1a1407] border border-[#E5B842]/50 text-[#E5B842] hover:bg-[#241c0a]" style={{ transition: "background-color 0.15s ease" }}>
                  <Shield className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link to={dashPath} data-testid="nav-dashboard" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#141414] border border-[rgba(212,175,55,0.22)] text-slate-200 hover:border-[#E5B842]/50 hover:text-[#E5B842]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <Link to="/settings" data-testid="nav-settings" className="p-2 rounded-lg text-slate-400 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }} title="Settings">
                <Settings className="w-4 h-4" />
              </Link>
              <button data-testid="nav-logout" onClick={() => { logout(); nav("/"); }} className="p-2 rounded-lg text-slate-400 hover:text-rose-400" style={{ transition: "color 0.15s ease" }} title="Log out">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-semibold text-slate-100 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
                Log in
              </Link>
              <Link to="/register" data-testid="nav-register" className="px-5 py-2 rounded-md text-sm font-bold uppercase tracking-wide bg-[#E5B842] text-[#000000] hover:bg-[#FFD700]" style={{ transition: "background-color 0.15s ease, box-shadow 0.15s ease", boxShadow: "0 0 20px rgba(229,184,66,0.30)" }}>
                Register
              </Link>
            </>
          )}
        </div>

        <button className={`${hamCls} ml-auto p-2 text-slate-100`} onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        {user && <div className={hamCls}><NotificationBell /></div>}
      </div>

      {open && (
        <div className={`${hamCls} border-t border-[rgba(212,175,55,0.22)] bg-[#000000] px-5 py-3 space-y-1 max-h-[75vh] overflow-y-auto`} data-testid="nav-mobile-menu">
          <form onSubmit={doSearch} className="relative mb-2">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="nav-search-input-mobile" placeholder="Search name or WSTF ID" className="w-full bg-[#141414] border border-[rgba(212,175,55,0.22)] rounded-lg pl-9 pr-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-[#E5B842]/60" />
          </form>
          {[...NAV, ...MORE].map((n) => (
            <Link key={n.to + n.label} to={n.to} onClick={() => setOpen(false)} className={`block px-3 py-2 rounded-md text-sm font-bold uppercase tracking-wide hover:text-[#E5B842] hover:bg-[#1a1407] ${isActive(n.to) ? "text-[#E5B842]" : "text-slate-200"}`}>
              {n.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-[rgba(212,175,55,0.22)] flex gap-2">
            {user ? (
              <>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setOpen(false)} data-testid="nav-admin-mobile" className="px-3 py-2 rounded-lg bg-[#1a1407] border border-[#E5B842]/50 text-[#E5B842] text-sm font-bold inline-flex items-center gap-1.5"><Shield className="w-4 h-4" /> Admin</Link>
                )}
                <Link to={dashPath} onClick={() => setOpen(false)} className="flex-1 text-center px-3 py-2 rounded-lg bg-[#141414] border border-[rgba(212,175,55,0.22)] text-slate-200 text-sm font-semibold">Dashboard</Link>
                <Link to="/settings" onClick={() => setOpen(false)} data-testid="nav-settings-mobile" className="px-3 py-2 rounded-lg bg-[#141414] border border-[rgba(212,175,55,0.22)] text-slate-200 text-sm font-semibold inline-flex items-center gap-1.5"><Settings className="w-4 h-4" /> Settings</Link>
                <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="px-3 py-2 rounded-lg text-rose-400 text-sm font-semibold">Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center px-3 py-2 rounded-lg bg-[#141414] border border-[rgba(212,175,55,0.22)] text-slate-200 text-sm font-semibold">Log in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="flex-1 text-center px-3 py-2 rounded-lg bg-[#E5B842] text-[#000000] text-sm font-bold">Register</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
